import React, { useState } from "react";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { SECTION_ORDER, sectionMeta } from "@/web/sections";
import { FieldRenderer, Lbl } from "./FieldRenderer";

const Icon = ({ name, className }) => { const C = Icons[name] || Icons.Square; return <C className={className} />; };

function newId() { return Math.random().toString(36).slice(2, 12); }

// Phase 82 — liste des sections (activer, réordonner, ajouter) + éditeur de la section sélectionnée
export function SectionsPanel({ sections, onChange, ownerKind, webApi }) {
  const [selected, setSelected] = useState(sections[0]?.id || null);
  const [adding, setAdding] = useState(false);
  const current = sections.find((s) => s.id === selected);

  const update = (id, patch) => onChange(sections.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const move = (id, dir) => { const i = sections.findIndex((s) => s.id === id); const j = i + dir; if (i < 0 || j < 0 || j >= sections.length) return; const arr = [...sections]; [arr[i], arr[j]] = [arr[j], arr[i]]; onChange(arr); };
  const remove = (id) => { onChange(sections.filter((s) => s.id !== id)); if (selected === id) setSelected(null); };
  const add = (type) => {
    const meta = sectionMeta(type);
    const defaults = Object.fromEntries((meta.fields || []).map((f) => [f.key, f.type === "list" || f.type === "images" || f.type === "tags" ? [] : f.type === "toggle" ? true : f.type === "select" ? f.options[0].value : ""]));
    if (type === "hours") defaults.days = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"].map((d, i) => ({ day: d, open: "09:00", close: "18:00", closed: i >= 5 }));
    const s = { id: newId(), type, enabled: true, title: meta.label, subtitle: "", data: defaults };
    onChange([...sections, s]); setSelected(s.id); setAdding(false);
  };
  const available = SECTION_ORDER.filter((t) => { const m = sectionMeta(t); if (m.kinds && !m.kinds.includes(ownerKind)) return false; return !(m.unique && sections.some((s) => s.type === t)); });

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-4" data-testid="sections-panel">
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#495057]">Sections de la page ({sections.length})</div>
        {sections.map((s, i) => {
          const m = sectionMeta(s.type);
          return (
            <div key={s.id} className={`flex items-center gap-2 px-3 py-2 border cursor-pointer ${selected === s.id ? "border-[#002FA7] bg-[#002FA7]/5" : "border-[#DEE2E6] bg-white hover:border-[#0A0A0A]"} ${s.enabled ? "" : "opacity-60"}`}
                 onClick={() => setSelected(s.id)} data-testid={`section-row-${s.type}`}>
              <Icon name={m.icon} className="h-4 w-4 text-[#002FA7] shrink-0" />
              <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{s.title || m.label}</div><div className="text-[10px] text-[#868E96] uppercase tracking-wider">{m.label}</div></div>
              <Switch checked={s.enabled} onCheckedChange={(v) => update(s.id, { enabled: v })} onClick={(e) => e.stopPropagation()} data-testid={`section-toggle-${s.type}`} />
              <div className="flex flex-col">
                <button type="button" onClick={(e) => { e.stopPropagation(); move(s.id, -1); }} disabled={i === 0} className="text-[#868E96] hover:text-black disabled:opacity-20"><Icons.ChevronUp className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={(e) => { e.stopPropagation(); move(s.id, 1); }} disabled={i === sections.length - 1} className="text-[#868E96] hover:text-black disabled:opacity-20"><Icons.ChevronDown className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          );
        })}
        {adding ? (
          <div className="border border-[#DEE2E6] bg-white p-2 space-y-1" data-testid="section-add-list">
            {available.map((t) => { const m = sectionMeta(t); return (
              <button key={t} type="button" onClick={() => add(t)} className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-[#F1F3F5]" data-testid={`section-add-${t}`}>
                <Icon name={m.icon} className="h-3.5 w-3.5 text-[#002FA7]" /><span className="font-semibold">{m.label}</span></button>); })}
            <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(false)} className="w-full h-7 text-xs">Annuler</Button>
          </div>
        ) : (
          <Button type="button" variant="outline" onClick={() => setAdding(true)} className="w-full h-10 border-dashed" data-testid="section-add-btn"><Icons.Plus className="h-4 w-4 mr-1" /> Ajouter une section</Button>
        )}
      </div>

      <div className="bg-white border border-[#DEE2E6] p-5 min-h-[300px]" data-testid="section-editor">
        {!current ? <div className="text-sm text-[#868E96]">Sélectionnez une section à gauche pour la modifier.</div> : (
          <SectionEditor section={current} onChange={(patch) => update(current.id, patch)} onRemove={() => remove(current.id)} webApi={webApi} />
        )}
      </div>
    </div>
  );
}

function SectionEditor({ section, onChange, onRemove, webApi }) {
  const meta = sectionMeta(section.type);
  const setData = (k, v) => onChange({ data: { ...section.data, [k]: v } });
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-bold text-lg flex items-center gap-2"><Icon name={meta.icon} className="h-5 w-5 text-[#002FA7]" /> {meta.label}</div>
          <p className="text-xs text-[#495057] mt-1">{meta.help}</p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={() => { if (window.confirm("Retirer cette section ?")) onRemove(); }} className="h-8 text-[#E60000] border-[#E60000]" data-testid="section-remove"><Icons.Trash2 className="h-3 w-3 mr-1" /> Retirer</Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1"><Lbl>Titre de la section</Lbl><Input value={section.title || ""} onChange={(e) => onChange({ title: e.target.value })} className="h-9 text-sm" data-testid="section-title" /></div>
        <div className="space-y-1"><Lbl>Sous-titre</Lbl><Input value={section.subtitle || ""} onChange={(e) => onChange({ subtitle: e.target.value })} className="h-9 text-sm" data-testid="section-subtitle" /></div>
      </div>
      <div className="space-y-3">
        {(meta.fields || []).map((f) => <FieldRenderer key={f.key} field={f} value={section.data?.[f.key]} onChange={(v) => setData(f.key, v)} webApi={webApi} />)}
      </div>
    </div>
  );
}
